/**
 * Session filter for API Logs report — Angular key: `apilogs_filter`.
 */

import { dmyToYmd } from './reportDateParams'
import type { ApiLogsListPatch } from '../types/utils'
import { API_LOGS_SESSION_KEY } from '../constants/sessionKeys'

export { API_LOGS_SESSION_KEY }

export function readApiLogsSession(): Record<string, unknown> | null {
  try {
    const raw = sessionStorage.getItem(API_LOGS_SESSION_KEY)
    if (!raw) return null
    const v = JSON.parse(raw) as unknown
    return typeof v === 'object' && v !== null && !Array.isArray(v) ? (v as Record<string, unknown>) : null
  } catch {
    return null
  }
}

export function writeApiLogsSession(data: Record<string, unknown>): void {
  sessionStorage.setItem(API_LOGS_SESSION_KEY, JSON.stringify(data))
}

export function clearApiLogsSession(): void {
  sessionStorage.removeItem(API_LOGS_SESSION_KEY)
}

function normalizeStoredDate(value: unknown): string {
  if (value == null) return ''
  const s = String(value).trim()
  if (!s) return ''
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s
  const fromDmy = dmyToYmd(s)
  return fromDmy || s
}

export function sessionFilterToApiLogsPatch(session: Record<string, unknown>): ApiLogsListPatch {
  const patch: ApiLogsListPatch = {}
  if (typeof session.page === 'number' && session.page > 0) patch.page = session.page
  if (typeof session.perPage === 'number' && session.perPage > 0) patch.perPage = session.perPage
  if (session.sortBy === 'asc' || session.sortBy === 'desc') patch.sortBy = session.sortBy
  if (typeof session.orderBy === 'string' && session.orderBy.trim()) patch.orderBy = session.orderBy
  if (typeof session.q === 'string') patch.q = session.q
  if (session.property_id != null && session.property_id !== '') {
    patch.property_id = String(session.property_id)
  }
  const fromDt = normalizeStoredDate(session.fromDt)
  const toDt = normalizeStoredDate(session.toDt)
  if (fromDt) patch.fromDt = fromDt
  if (toDt) patch.toDt = toDt
  return patch
}

function ymdToDmy(ymd: string): string {
  if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return ''
  const [y, m, d] = ymd.split('-')
  return `${d}-${m}-${y}`
}

export function buildApiLogsSessionFromParams(p: {
  page: number
  perPage: number
  sortBy: string
  orderBy: string
  q: string
  property_id: string
  fromDt: string
  toDt: string
}): Record<string, unknown> {
  const out: Record<string, unknown> = {
    page: p.page,
    perPage: p.perPage,
    sortBy: p.sortBy,
    orderBy: p.orderBy,
    q: p.q,
  }
  if (p.property_id) out.property_id = p.property_id
  const fd = ymdToDmy(p.fromDt)
  const td = ymdToDmy(p.toDt)
  if (fd) out.fromDt = fd
  if (td) out.toDt = td
  return out
}
